import { Close } from '@mui/icons-material';
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { SlidingPanel } from '@side/pantry';
import { useEffect, useRef } from 'react';
import { useAIInsightsChat } from '../hooks/useAIInsightsChat';
import {
  STARTER_PROMPTS,
  scrollTranscriptToBottom,
} from './AIInsightsButton.helpers';
import { AIInsightsControls } from './AIInsightsControls';
import { AIInsightsMessage } from './AIInsightsMessage';
import { AIInsightsStarterPrompt } from './AIInsightsStarterPrompt';

export const AIInsightsPanel = ({
  open,
  onClose,
  viewReportAs,
}: {
  open: boolean;
  onClose: () => void;
  viewReportAs: string;
}) => {
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const {
    messages,
    interactionId,
    inputMessage,
    setInputMessage,
    isBusy,
    isLoading,
    isRestoringHistory,
    canSendMessage,
    loadingMessage,
    hasAssistantInsight,
    runPrompt,
    sendFollowUp,
    clearChat,
  } = useAIInsightsChat({
    isOpen: open,
    viewReportAs,
  });

  useEffect(() => {
    if (!open || !transcriptRef.current) {
      return;
    }

    // Keep the most recent message visible after async updates and user input.
    const animationFrameId = requestAnimationFrame(() => {
      scrollTranscriptToBottom(transcriptRef.current);
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [open, isLoading, isRestoringHistory, messages]);

  return (
    <SlidingPanel
      open={open}
      onClose={onClose}
      anchor="right"
      DrawerProps={{
        slotProps: {
          paper: {
            sx: {
              backgroundColor: 'other.spaceCadet',
            },
          },
        },
      }}
      ContentProps={{
        sx: {
          p: 0,
          height: '100dvh',
          overflow: 'hidden',
          '& > .MuiBox-root:first-of-type': {
            display: 'none',
          },
        },
      }}
    >
      <Stack sx={{ minHeight: 0, height: '100%' }}>
        <Stack
          sx={(theme) => ({
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            pr: 1,
            pl: 2,
            backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.25) 100%), linear-gradient(90deg, #A549C1 0%, #824FC5 25%, #5F55C8 50%, #3B5ACB 75%, ${theme.palette.primary.main} 100%)`,
          })}
        >
          <Typography
            variant="h4"
            component="span"
            sx={{ color: 'common.white', mr: 'auto' }}
          >
            AI Insights
          </Typography>
          <IconButton onClick={onClose}>
            <Close sx={{ color: 'common.white' }} />
          </IconButton>
        </Stack>

        <Stack
          sx={{
            minHeight: 0,
            display: 'flex',
            flex: 1,
          }}
        >
          <Stack
            ref={transcriptRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              gap: 2.5,
              minHeight: 0,
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {messages.map((message) => (
              <AIInsightsMessage
                key={message.id}
                message={message}
                geminiInteractionId={interactionId}
              />
            ))}

            {!hasAssistantInsight && !isBusy && (
              <Stack sx={{ gap: 1.5, pt: 0.5 }}>
                {STARTER_PROMPTS.map((prompt) => (
                  <AIInsightsStarterPrompt
                    key={prompt.id}
                    prompt={prompt}
                    onRunPrompt={(promptId, label) => {
                      void runPrompt(promptId, label);
                    }}
                    disabled={isBusy}
                  />
                ))}
              </Stack>
            )}

            {isBusy && (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 1,
                  color: 'common.white',
                }}
              >
                <CircularProgress size={18} sx={{ color: 'inherit' }} />
                <Typography variant="body2">{loadingMessage}</Typography>
              </Stack>
            )}
          </Stack>

          <AIInsightsControls
            isBusy={isBusy}
            starterPrompts={STARTER_PROMPTS}
            inputMessage={inputMessage}
            canSendMessage={canSendMessage}
            onInputMessageChange={setInputMessage}
            onClearChat={clearChat}
            onRunPrompt={(promptId, label) => {
              void runPrompt(promptId, label);
            }}
            onSendFollowUp={() => {
              void sendFollowUp();
            }}
          />
        </Stack>
      </Stack>
    </SlidingPanel>
  );
};
